import { ReactNode } from 'react'
import {
  isPrimaryBillingContact,
  receivesBillingNotifications
} from 'CustomerContacts/utils/contactPurposes'
import { Dashboardv99990101CommonModels } from '@sequencehq/api/dashboard/v99990101'
import {
  Text,
  Badge,
  IconBuildings,
  IconEmail,
  GreyGrey20,
  GreyGrey30,
  GreyGrey60,
  GreyGrey70,
  Primary13Regular,
  Primary13SemiBold,
  TextOverflow,
  Box,
  Flex
} from '@sequencehq/core-components'
import {
  ComparisonCard,
  ComparisonVariant,
  getComparisonCardFrame,
  getComparisonCardStyle
} from './BeforeAfterComparison'

type CustomerContact = Dashboardv99990101CommonModels.Customers.CustomerContact

/**
 * A single billing-contact card (avatar, name, email, company + invoice footer)
 * styled for its variant. Used both in the review modal's before/after
 * comparison and, in its `after` form, in the inbox preview so the two read
 * consistently. When `contact` is missing it renders either the "No billing
 * contact" placeholder or nothing, depending on `showEmptyPlaceholder`.
 */
export const ContactCard = ({
  contact,
  variant,
  companyName,
  showEmptyPlaceholder,
  isNew = false,
  highlighted = false,
  'data-testid': dataTestId
}: {
  contact?: CustomerContact
  variant: ComparisonVariant
  companyName?: string
  'data-testid'?: string
  /**
   * When this contact is missing, whether to render the "No billing contact"
   * placeholder. True only when this side has no contacts at all; otherwise the
   * cell is left empty (the contact was added/removed on the other side).
   */
  showEmptyPlaceholder: boolean
  /**
   * Whether this contact is being added by the proposed change. Shows a "New"
   * badge on the card — used by the inbox preview's flat card list. The review
   * modal's before/after comparison labels added contacts at the row level
   * instead, so it leaves this off.
   */
  isNew?: boolean
  /** Rings the card in indigo when its form is the one being edited. */
  highlighted?: boolean
}) => {
  const { color } = getComparisonCardStyle(variant, highlighted)

  if (!contact) {
    if (!showEmptyPlaceholder) {
      return null
    }

    return (
      <ComparisonCard variant={variant} data-testid={dataTestId}>
        <Text {...Primary13Regular} color={GreyGrey60}>
          No billing contact
        </Text>
      </ComparisonCard>
    )
  }

  const isPrimary = isPrimaryBillingContact(contact)
  const receivesInvoices = receivesBillingNotifications(contact)

  return (
    <Box
      {...getComparisonCardFrame(variant, highlighted)}
      width="100%"
      // Matches `ComparisonCard`: fill the row so this card and the one it is
      // compared against stay the same height.
      height="100%"
      overflow="hidden"
      data-testid={dataTestId}
    >
      <Flex
        justifyContent="space-between"
        alignItems="flex-start"
        gap="12px"
        padding="16px"
      >
        <Flex gap="12px" alignItems="center" minWidth="0">
          <ContactAvatar name={contact.name} />
          <Flex flexDirection="column" gap="4px" minWidth="0">
            {/* Box (not Text) wrapper: TextOverflow renders a div — invalid
                inside Text's <p> — and reads its font from the computed style of
                its parent, so the typography token must live on this wrapper. */}
            <Box {...Primary13SemiBold} color={color} width="100%">
              <TextOverflow>{contact.name}</TextOverflow>
            </Box>
            <Box {...Primary13Regular} color={color} width="100%">
              <TextOverflow>{contact.email}</TextOverflow>
            </Box>
          </Flex>
        </Flex>
        {(isNew || isPrimary) && (
          <Flex gap="4px" flexShrink={0}>
            {isNew && (
              <Badge sentiment="info" textTransform="none">
                New
              </Badge>
            )}
            {isPrimary && (
              <Badge sentiment="neutral" textTransform="none">
                Primary
              </Badge>
            )}
          </Flex>
        )}
      </Flex>
      {(companyName || receivesInvoices) && (
        <Flex
          flexDirection="column"
          borderTop="1px solid"
          borderColor={GreyGrey30}
          paddingX="16px"
          paddingY="12px"
        >
          {companyName && (
            <ContactDetailRow icon={<IconBuildings />} color={color}>
              {companyName}
            </ContactDetailRow>
          )}
          {receivesInvoices && (
            <ContactDetailRow icon={<IconEmail />} color={color}>
              Receives invoices
            </ContactDetailRow>
          )}
        </Flex>
      )}
    </Box>
  )
}

/** Grey circle with the contact's initials, e.g. "Tim Cook" → "TC". */
const ContactAvatar = ({ name }: { name?: string }) => (
  <Flex
    flexShrink={0}
    alignItems="center"
    justifyContent="center"
    width="48px"
    height="48px"
    borderRadius="full"
    background={GreyGrey20}
  >
    <Text {...Primary13SemiBold} color={GreyGrey70}>
      {getInitials(name)}
    </Text>
  </Flex>
)

/** A footer detail row: a leading 16px icon and a truncating label. */
const ContactDetailRow = ({
  icon,
  color,
  children
}: {
  icon: ReactNode
  color: string
  children: string
}) => (
  <Flex gap="12px" alignItems="center" height="28px">
    <Flex color={GreyGrey70} flexShrink={0}>
      {icon}
    </Flex>
    <Box {...Primary13Regular} color={color} flex="1" minWidth="0">
      <TextOverflow>{children}</TextOverflow>
    </Box>
  </Flex>
)

const getInitials = (name?: string): string => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)

  if (parts.length === 0) {
    return '?'
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase()
  }

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}
